import type { IndexTrackDto } from "../api";
import { FieldHelp } from "./FieldHelp";

/** Index counterparts to the stock Return and Volatility provenance panels,
 * laid out in the same two-column row. An index has no per-segment return
 * model by design (neutral drift); the Return card says so with the measured
 * reason instead of an empty box. The Volatility card carries the sigma-
 * persistence regime and the forward-range fan. */

const REGIME_LABEL: Record<string, string> = {
  ADEQUATE: "tracking within its normal range",
  DEGRADED: "tracking worse than normal, not at crash level",
  REGIME_BREAK: "volatility input currently unreliable",
  NO_DATA: "no resolved history yet to monitor against",
};

function pct(v: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;
}

function FanChart({ days, fan }: { days: number[]; fan: Record<string, number[]> }) {
  const p10 = fan["10"] ?? [];
  const p50 = fan["50"] ?? [];
  const p90 = fan["90"] ?? [];
  if (days.length < 2 || p50.length < 2) return null;
  const W = 480, H = 120, pad = { l: 40, r: 10, t: 8, b: 18 };
  const yMin = Math.min(...p10, 0), yMax = Math.max(...p90, 0);
  const span = yMax - yMin || 1;
  const x = (i: number) => pad.l + (i / (days.length - 1)) * (W - pad.l - pad.r);
  const y = (v: number) => pad.t + (1 - (v - yMin) / span) * (H - pad.t - pad.b);
  const line = (arr: number[]) => arr.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const band = `${line(p90)} ${p10.map((_v, i) => `L${x(p10.length - 1 - i).toFixed(1)},${y(p10[p10.length - 1 - i]).toFixed(1)}`).join(" ")} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }} role="img"
      aria-label="Forward range fan: 10th, 50th and 90th percentile paths over the horizon">
      <path d={band} fill="#2563eb22" stroke="none" />
      <line x1={pad.l} x2={W - pad.r} y1={y(0)} y2={y(0)} stroke="#96a0aa" strokeDasharray="3 3" />
      <path d={line(p50)} fill="none" stroke="#2563eb" strokeWidth={1.5} />
      <path d={line(p90)} fill="none" stroke="#16a34a" strokeWidth={1} />
      <path d={line(p10)} fill="none" stroke="#dc2626" strokeWidth={1} />
      {[yMax, 0, yMin].map((v) => (
        <text key={v} x={pad.l - 4} y={y(v) + 3} textAnchor="end" fontSize={9} fill="#96a0aa"
          fontFamily="IBM Plex Mono, monospace">{pct(v)}</text>
      ))}
      <text x={pad.l} y={H - 4} fontSize={9} fill="#96a0aa" fontFamily="IBM Plex Mono, monospace">today</text>
      <text x={W - pad.r} y={H - 4} textAnchor="end" fontSize={9} fill="#96a0aa"
        fontFamily="IBM Plex Mono, monospace">+{days[days.length - 1]} trading days</text>
    </svg>
  );
}

export function IndexReturnCard({ track }: { track: IndexTrackDto }) {
  return (
    <div className="detail-card">
      <div className="detail-card-head">
        <span>Return <FieldHelp term="index_track" /></span>
        <span className="note">by design</span>
      </div>
      <div className="mgrid" style={{ marginBottom: 8 }}>
        <div className="met">
          <span className="k">Drift</span>
          <span className="v">neutral</span>
          <span className="sub">centred on zero by design</span>
        </div>
      </div>
      <div className="prov-note">
        No directional signal is shown, because none was found: 0 of 42 blessed features cleared
        significance at h=70 or h=5. Showing one would be fabricated. {track.neutral_drift_note}
      </div>
    </div>
  );
}

export function IndexVolatilityCard({ track }: { track: IndexTrackDto }) {
  const status = track.regime.status ?? "NO_DATA";
  const sigma = track.persistence_sigma_annual;
  return (
    <div className="detail-card">
      <div className="detail-card-head">
        <span>Volatility <FieldHelp term="index_track" /></span>
        <span className="note">persistence range</span>
      </div>
      <div className="mgrid" style={{ marginBottom: 8 }}>
        <div className="met">
          <span className="k">Regime</span>
          <span className="v">{status}</span>
          <span className="sub">{REGIME_LABEL[status] ?? status}</span>
        </div>
        <div className="met">
          <span className="k">Persistence sigma</span>
          <span className="v">{sigma !== null ? `${(sigma * 100).toFixed(1)}%` : "--"}</span>
          <span className="sub">annualised, trailing {track.persistence_window_days}d</span>
        </div>
      </div>
      {sigma !== null ? (
        <>
          <div className="note" style={{ marginBottom: 2 }}>
            Forward range, {track.horizon_days} trading days (10th / 50th / 90th percentile)
          </div>
          <FanChart days={track.fan_days} fan={track.fan} />
          <div className="prov-note" style={{ marginTop: 4 }}>
            Understates tail risk at a regime break, which the regime chip above monitors.
          </div>
        </>
      ) : (
        <div className="prov-note">No volatility history yet to build a forward range from.</div>
      )}
    </div>
  );
}
